import { Button } from '@repo/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { sortByKey } from '@repo/ui/utils'
import { MailsIcon, SearchIcon, SearchXIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Document } from '@/components/beneficiary/Document'
import {
  DocumentScanner,
  DocumentUploader,
  NoteCreator,
} from '@/components/beneficiary/DocumentCreator'
import { buildAnchorIdPrefix } from '@/components/helpers'
import { Matches } from '@/components/search/helpers'
import { HighlightedText } from '@/components/search/HighlightedText'
import { Search } from '@/components/search/Search'
import { useCopyToClipboard } from '@/hooks'
import { AvailableParams } from '@/routes/router'
import { Document as DocumentType } from '@/store/document'
import { useFeatureFlags } from '@/store/feature-flags/hooks'
import { getCurrentOrganization } from '@/store/helpers'

function EmailAnalysis() {
  const { tc } = useT()
  const { beneficiaryId } = useParams<AvailableParams>()
  const { hasFeature } = useFeatureFlags()
  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])

  const region = import.meta.env.VITE_REGION as string
  const email = `${beneficiaryId}@mail.${region}.caseai.org`

  const { copy, isCopied } = useCopyToClipboard(tc('successfullyCopied', { value: email }))

  if (!beneficiaryId || !organization || !region || !hasFeature('emailAnalysis')) return null
  else
    return (
      <TooltipWrapper content={tc('copyEmailAnalysis')}>
        <Button size="icon" variant="outline" disabled={isCopied} onClick={() => void copy(email)}>
          <MailsIcon className="size-4" />
        </Button>
      </TooltipWrapper>
    )
}

export function Documents({
  description,
  documents,
  isFile = false,
  title,
}: {
  description: string
  documents: DocumentType[]
  isFile?: boolean
  title: string
}) {
  const { t, tc } = useT({ keyPrefix: 'documents' })
  const [isSearching, setIsSearching] = useState(false)
  const sortedDocuments = useMemo(
    () => [...documents].sort(sortByKey('createdAt', 'desc')),
    [documents],
  )
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription className="flex items-center gap-2">{description}</CardDescription>

        <CardAction>
          <TooltipWrapper content={tc(isSearching ? 'close' : 'search', { cfl: true })}>
            <Button
              variant={isSearching ? 'default' : 'outline'}
              size="icon"
              onClick={() => setIsSearching(!isSearching)}
            >
              {isSearching ? <SearchXIcon className="size-4" /> : <SearchIcon className="size-4" />}
            </Button>
          </TooltipWrapper>

          {isFile ? (
            <DocumentUploader />
          ) : (
            <>
              <NoteCreator />
              <EmailAnalysis />
              <DocumentScanner />
            </>
          )}
        </CardAction>
      </CardHeader>

      {documents.length === 0 ? (
        <CardContent className="text-primary-400">
          {t(isFile ? 'emptyDocumentDescription' : 'emptyNoteDescription')}
        </CardContent>
      ) : (
        <Search disabled={!isSearching} items={sortedDocuments}>
          {({ query, searchResults, currentMatch, matchRefs }) => (
            <CardContent className="pt-0 xl:pt-0">
              <div className="flex flex-col gap-6 pt-4 xl:pt-6">
                {query.length > 1 ? (
                  <>
                    {searchResults.length > 0 ? (
                      searchResults.map(
                        ({ item: document, matches }: { item: DocumentType; matches: unknown }) => {
                          return (
                            <AugmentedDocument
                              key={document.id}
                              currentMatch={currentMatch}
                              findIndex={(anchorPrefix) =>
                                matchRefs.current.findIndex((id) => id.startsWith(anchorPrefix))
                              }
                              matches={matches as Matches<'title' | 'content'>}
                              document={document}
                            />
                          )
                        },
                      )
                    ) : (
                      <div className="text-center">{tc('noResults', { cfl: true })}</div>
                    )}
                  </>
                ) : (
                  sortedDocuments.map((doc) => <Document key={doc.id} document={doc} />)
                )}
              </div>
            </CardContent>
          )}
        </Search>
      )}
    </Card>
  )
}

function AugmentedDocument({
  currentMatch,
  findIndex,
  matches,
  document,
}: {
  currentMatch: number
  findIndex: (id: string) => number
  matches: Matches<'title' | 'content'>
  document: DocumentType
}) {
  const key = 'content' in document ? 'content' : 'title'
  const contentIndices = matches[key]
  const anchorPrefixes = buildAnchorIdPrefix(document.id, key)
  const getCurrentMatchIndex = () => {
    const matchIdx = findIndex(anchorPrefixes)
    return matchIdx >= 0 ? currentMatch - matchIdx : -1
  }
  const currentMatchIndex = getCurrentMatchIndex()
  const text =
    key === 'content'
      ? ((document as { content?: string }).content ?? '')
      : ((document as { title?: string }).title ?? '')
  return (
    <Document
      document={document}
      contentComp={
        <HighlightedText
          text={text}
          indices={contentIndices}
          currentMatchIndex={currentMatchIndex}
          anchorPrefix={anchorPrefixes}
        />
      }
    />
  )
}
